import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  Button,
  CheckBox,
  Column,
  Heading,
  Icon,
  InputTyping,
  Label,
  Modal,
  Row,
  Select,
  Textarea,
} from '@drivesally/sally-ui';

import {grey} from '@drivesally/sally-ui/lib/libs/colors';

import fkOrId from '@drivesally/sally-fw/lib/lib/fkOrId';

import getTeamLabel from '../../lib/getTeamLabel.lib.crm';
import getUsername from '../../../user/lib/getUsername.lib.user';
import normalizeTags from '../../lib/normalizeTags.lib.crm';
import CrmTagPill from '../CrmTagPill/CrmTagPill';
import CrmTicketTags from '../CrmTicketTags/CrmTicketTags';

const Loader = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const CreateForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const AssignRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const TagsList = styled.div`
  max-height: 160px;
  overflow-y: auto;
`;

const TagRow = styled.div`
  align-items: center;
  border-bottom: 1px solid ${grey};
  cursor: pointer;
  display: flex;
  gap: 8px;
  padding: 6px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const TemplateLegend = styled.div`
  color: #555;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  gap: 6px;
  line-height: 1.5;
`;

const TemplateLegendLine = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TemplateToken = styled.code`
  background: #f0f0f0;
  border-radius: 4px;
  color: #222;
  font-family: ProximaNova, monospace;
  font-size: 13px;
  padding: 2px 6px;
`;

const ShortcutListHeader = styled.div`
  border-bottom: 1px solid ${grey};
  color: #888;
  display: flex;
  font-size: 12px;
  font-weight: 600;
  gap: 12px;
  padding: 0 0 8px;
  text-transform: uppercase;
`;

const ShortcutRow = styled.div`
  border-bottom: 1px solid ${grey};
  display: flex;
  gap: 12px;
  padding: 12px 0;

  &:last-child {
    border-bottom: none;
  }
`;

const ShortcutBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ShortcutName = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const ShortcutMessage = styled.div`
  color: #555;
  font-size: 13px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ShortcutColumn = styled.div`
  color: #555;
  flex-shrink: 0;
  font-size: 13px;
  min-width: 0;
  width: ${({wide}) => (wide ? '160px' : '120px')};
  word-break: break-word;
`;

const HeaderColumn = styled.div`
  flex-shrink: 0;
  width: ${({flex, wide}) => (flex ? 'auto' : wide ? '160px' : '120px')};
  ${({flex}) => flex && 'flex: 1;'}
`;

const DeleteButton = styled.button`
  align-items: center;
  align-self: flex-start;
  background: none;
  border: none;
  color: #888;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  padding: 4px;
  width: 24px;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover:not(:disabled) {
    color: #e74c3c;
  }
`;

const EmptyState = styled.div`
  color: #888;
  font-size: 14px;
  padding: 16px 0;
`;

const SectionSeparator = styled.div`
  border-top: 1px solid ${grey};
  margin-top: 8px;
`;

const getTeamDisplay = (shortcut, teamsById) => {
  const teamId = fkOrId(shortcut?.team);
  if (!teamId) return '-';
  const team = teamsById[teamId];
  return getTeamLabel(team) || `${teamId}`;
};

const getUserDisplay = (shortcut, users) => {
  const userId = fkOrId(shortcut?.user);
  if (!userId) return '-';
  return getUsername({userId, users});
};

const CrmShortcutsModal = ({
  assignToMyself,
  creating,
  deletingId,
  filterTeam,
  filterUser,
  loading,
  nameInputKey,
  newMessage,
  newName,
  newTeamId,
  onAssignToMyselfChange,
  onClose,
  onCreate,
  onDelete,
  onFilterTeamChange,
  onFilterUserChange,
  onNewMessageChange,
  onNewNameChange,
  onNewTeamChange,
  onSearch,
  onTagToggle,
  search,
  selectedTagIds,
  shortcuts,
  tags,
  tagsById,
  teams,
  teamsById,
  users,
  visible,
}) => (
  <Modal
    title="CRM Shortcuts"
    icon="filetext1"
    size="large"
    visible={visible}
    onClose={onClose}
  >
    {(Content) => (
      <Content>
        <Row margin>
          <Column>
            <CreateForm>
              <InputTyping
                key={nameInputKey}
                value={newName}
                onChange={onNewNameChange}
                placeholder="Shortcut name"
                disabled={creating || loading}
              />
              <TemplateLegend>
                <TemplateLegendLine>
                  <span>
                    First Name: <TemplateToken>{'{{first_name}}'}</TemplateToken>
                  </span>
                  <span>|</span>
                  <span>
                    Last Name: <TemplateToken>{'{{last_name}}'}</TemplateToken>
                  </span>
                </TemplateLegendLine>
                <TemplateLegendLine>
                  <span>
                    First Initial: <TemplateToken>{'{{first_initial}}'}</TemplateToken>
                  </span>
                  <span>|</span>
                  <span>
                    Last Initial: <TemplateToken>{'{{last_initial}}'}</TemplateToken>
                  </span>
                </TemplateLegendLine>
                <TemplateLegendLine>
                  <span>
                    Driver Name: <TemplateToken>{'{{driver_name}}'}</TemplateToken>
                  </span>
                  <span>|</span>
                  <span>
                    Branch Name: <TemplateToken>{'{{branch}}'}</TemplateToken>
                  </span>
                  <span>|</span>
                  <span>
                    Towing: <TemplateToken>{'{{towing}}'}</TemplateToken>
                  </span>
                  <span>|</span>
                  <span>
                    Office Hours: <TemplateToken>{'{{office_hours}}'}</TemplateToken>
                  </span>
                </TemplateLegendLine>
                <TemplateLegendLine>
                  <span>Office Address: <TemplateToken>{'{{office_addr}}'}</TemplateToken></span>
                  <span>|</span>
                  <span>Garage Address: <TemplateToken>{'{{garage_addr}}'}</TemplateToken></span>
                </TemplateLegendLine>
                
                
              </TemplateLegend>
              <Textarea
                value={newMessage}
                onChange={onNewMessageChange}
                disabled={creating || loading}
                placeholder="Hi {{driver_name}}, this is {{first_name}} {{last_initial}} how can I help you?"
              >
                Message
              </Textarea>
              <Label label="Team">
                <Select
                  value={newTeamId}
                  options={[
                    {value: '', label: 'None'},
                    ...teams.map((team) => ({
                      value: `${team.id}`,
                      label: getTeamLabel(team),
                    })),
                  ]}
                  onChange={onNewTeamChange}
                  disabled={creating || loading || assignToMyself}
                  notFilterable
                />
              </Label>
              <Label label="Tags">
                <TagsList>
                  {!tags.length ? (
                    <EmptyState>No tags available.</EmptyState>
                  ) : (
                    tags.map((tag) => (
                      <TagRow
                        key={tag.id}
                        onClick={
                          creating || loading
                            ? undefined
                            : onTagToggle(tag.id)
                        }
                      >
                        <CheckBox
                          value={selectedTagIds.includes(tag.id)}
                          onChange={() => {}}
                          disabled={creating || loading}
                          size="small"
                        />
                        <CrmTagPill
                          color={tag.color}
                          label={tag.tag}
                          small
                        />
                      </TagRow>
                    ))
                  )}
                </TagsList>
              </Label>
              <AssignRow>
                <CheckBox
                  value={assignToMyself}
                  onChange={onAssignToMyselfChange}
                  disabled={creating || loading}
                  size="small"
                />
                <span>Assign to me only</span>
              </AssignRow>
              <Button
                theme="red"
                loading={creating}
                disabled={loading}
                onClick={onCreate}
              >
                Add Shortcut
              </Button>
            </CreateForm>
          </Column>
        </Row>
        <Row margin>
          <Column>
            <SectionSeparator />
          </Column>
        </Row>
        <Row margin>
          <Column>
            <Heading size="3">Existing Shortcuts</Heading>
          </Column>
        </Row>
        <Row margin>
          <Column>
            <InputTyping
              key={visible ? 'search-open' : 'search-closed'}
              value={search}
              onChange={onSearch}
              placeholder="Search shortcuts"
              disabled={creating}
              treshold={400}
            />
          </Column>
        </Row>
        <Row margin>
          <Column size={1 / 2}>
            <Label label="Filter by team">
              <Select
                value={filterTeam}
                options={[
                  {value: 'all', label: 'All'},
                  {value: 'none', label: 'No team'},
                  ...teams.map((team) => ({
                    value: `${team.id}`,
                    label: getTeamLabel(team),
                  })),
                ]}
                onChange={onFilterTeamChange}
                disabled={loading}
                notFilterable
              />
            </Label>
          </Column>
          <Column size={1 / 2}>
            <Label label="Filter by user">
              <Select
                value={filterUser}
                options={[
                  {value: 'all', label: 'All'},
                  {value: 'mine', label: 'Mine only'},
                  {value: 'unassigned', label: 'No user assigned'},
                ]}
                onChange={onFilterUserChange}
                disabled={loading}
                notFilterable
              />
            </Label>
          </Column>
        </Row>
        <Row>
          <Column>
            {loading ? (
              <Loader>
                <Icon icon="loading2" spin />
              </Loader>
            ) : !shortcuts.length ? (
              <EmptyState>No shortcuts yet.</EmptyState>
            ) : (
              <>
                <ShortcutListHeader>
                  <HeaderColumn flex>Shortcut</HeaderColumn>
                  <HeaderColumn wide>Tags</HeaderColumn>
                  <HeaderColumn>Team</HeaderColumn>
                  <HeaderColumn>User</HeaderColumn>
                  <HeaderColumn style={{width: 24}} />
                </ShortcutListHeader>
                {shortcuts.map((shortcut) => (
                  <ShortcutRow key={shortcut.id}>
                    <ShortcutBody>
                      <ShortcutName>{shortcut.name}</ShortcutName>
                      <ShortcutMessage>{shortcut.message}</ShortcutMessage>
                    </ShortcutBody>
                    <ShortcutColumn wide>
                      <CrmTicketTags
                        tags={normalizeTags(shortcut.tags, tagsById)}
                      />
                    </ShortcutColumn>
                    <ShortcutColumn>
                      {getTeamDisplay(shortcut, teamsById)}
                    </ShortcutColumn>
                    <ShortcutColumn>
                      {getUserDisplay(shortcut, users)}
                    </ShortcutColumn>
                    <DeleteButton
                      type="button"
                      disabled={!!deletingId || creating}
                      onClick={onDelete(shortcut)}
                    >
                      <Icon
                        icon={deletingId === shortcut.id ? 'loading2' : 'delete'}
                        spin={deletingId === shortcut.id}
                      />
                    </DeleteButton>
                  </ShortcutRow>
                ))}
              </>
            )}
          </Column>
        </Row>
      </Content>
    )}
  </Modal>
);

CrmShortcutsModal.propTypes = {
  assignToMyself: PropTypes.bool,
  creating: PropTypes.bool,
  deletingId: PropTypes.number,
  filterTeam: PropTypes.string,
  filterUser: PropTypes.string,
  loading: PropTypes.bool,
  nameInputKey: PropTypes.number,
  newMessage: PropTypes.string,
  newName: PropTypes.string,
  newTeamId: PropTypes.string,
  onAssignToMyselfChange: PropTypes.func,
  onClose: PropTypes.func,
  onCreate: PropTypes.func,
  onDelete: PropTypes.func,
  onFilterTeamChange: PropTypes.func,
  onFilterUserChange: PropTypes.func,
  onNewMessageChange: PropTypes.func,
  onNewNameChange: PropTypes.func,
  onNewTeamChange: PropTypes.func,
  onSearch: PropTypes.func,
  onTagToggle: PropTypes.func,
  search: PropTypes.string,
  selectedTagIds: PropTypes.array,
  shortcuts: PropTypes.array,
  tags: PropTypes.array,
  tagsById: PropTypes.object,
  teams: PropTypes.array,
  teamsById: PropTypes.object,
  users: PropTypes.array,
  visible: PropTypes.bool,
};

CrmShortcutsModal.defaultProps = {
  selectedTagIds: [],
  tags: [],
  tagsById: {},
};

export default CrmShortcutsModal;
